import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Check, ArrowRight, Sparkles, ShieldCheck, HelpCircle, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import { fetchPlanCatalog, PLAN_FALLBACK, COMPARATOR } from '@/lib/plans';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCIES = [
    { key: 'EUR', label: '€ EUR', rate: 1 },
    { key: 'XOF', label: 'CFA XOF', rate: 656 },
    { key: 'XAF', label: 'CFA XAF', rate: 656 },
    { key: 'USD', label: '$ USD', rate: 1.08 },
];

const FAQ = [
    {
        q: "Je viens d'obtenir une subvention ou un financement : par où commencer ?",
        a: "Félicitations — c'est exactement pour ce moment qu'African Valley 360° a été conçu. Créez gratuitement votre compte Explorer (sans carte bancaire), enregistrez votre projet avec son budget et ses jalons, invitez jusqu'à 3 collaborateurs et rattachez votre convention de financement dans la GED. En moins d'une heure, votre projet est structuré : chaque dépense, chaque échéance et chaque document est tracé dès le premier jour — la meilleure garantie d'un projet serein jusqu'au rapport final.",
    },
    {
        q: 'Comment rendre compte à mon bailleur tout au long du projet ?',
        a: "La plateforme génère en 1 clic des rapports PDF, Excel et PowerPoint conformes aux attentes des grands bailleurs (AFD, BAD, Banque mondiale, UE…) : avancement des jalons, budget engagé/réalisé/décaissé, justificatifs rattachés, indicateurs d'impact et tranches de subvention reçues ou en retard. Vous rendez compte à chaque étape sans re-saisie ni stress de dernière minute — les données sont déjà là, à jour, tracées dans le journal d'audit.",
    },
    {
        q: "Qu'est-ce que le REX (retour d'expérience) et comment la plateforme m'aide à le produire ?",
        a: "Le REX est le document de capitalisation remis en fin de projet : ce qui a fonctionné, les difficultés rencontrées, les leçons apprises et les recommandations. C'est lui qui crédibilise votre structure pour les financements suivants. Comme African Valley 360° a tracé tout le cycle du projet (jalons, budget, décisions de comités, documents, indicateurs), votre REX se construit sur des faits horodatés et non sur des souvenirs — avec l'appui de Valley AI pour la synthèse. Résultat : un REX de qualité supérieure, produit en jours et non en semaines.",
    },
    {
        q: 'Qu\'est-ce que l\'African Valley Project Score™ ?',
        a: "Un diagnostic gratuit de la maturité de votre projet en 3 minutes : 8 dimensions clés (vision, gouvernance, planification, budget, conformité, impact, risques, ancrage local) notées sur 100 points, avec un niveau, des recommandations ciblées et un plan d'action généré par Valley AI. Résultat immédiat et partageable, sans créer de compte. Une fois client, vous pouvez réévaluer chaque projet depuis son tableau de bord et suivre l'évolution du score — il apparaît aussi dans vos rapports PDF bailleurs.",
    },
    {
        q: 'Comment se passe une démonstration ?',
        a: "Remplissez le formulaire de démonstration (60 secondes) en précisant votre besoin principal et l'offre envisagée : un expert vous recontacte sous 48 h ouvrées et vous montre la plateforme sur VOS cas d'usage — reporting bailleurs, budget multi-devises, gouvernance multi-entités… Gratuit et sans engagement.",
    },
    {
        q: 'Le plan Explorer est-il vraiment gratuit pour toujours ?',
        a: "Oui. Explorer est un freemium permanent : 1 organisation, 1 projet actif et 3 utilisateurs, gratuits sans limite de durée et sans carte bancaire. Aucun renouvellement à faire — votre compte reste actif tant que vous l'utilisez.",
    },
    {
        q: 'Y a-t-il un essai gratuit sur les offres payantes ?',
        a: "Oui, 14 jours d'essai gratuit sur les offres Essentiel, Impact et Organisation via Stripe. Vous recevez un email de rappel 3 jours avant la fin de l'essai, puis l'abonnement démarre automatiquement. Annulation possible à tout moment depuis le portail client Stripe.",
    },
    {
        q: 'Quelle différence entre paiement mensuel et annuel ?',
        a: "Le paiement annuel vous fait économiser 2 mois sur 12 (Essentiel : 390 € au lieu de 468 € ; Impact : 990 € au lieu de 1 188 € ; Organisation : 2 490 € au lieu de 2 988 €). Le paiement en ligne est disponible en libre-service via Stripe.",
    },
    {
        q: "Comment fonctionnent les options à l'unité ?",
        a: "Marque blanche (100 €), +2 espaces (50 €) ou +5 utilisateurs (50 €) : paiement unique via Stripe depuis votre tableau de bord (bouton « Upgrade plan » → Options à l'unité). L'option est activée instantanément après confirmation du paiement.",
    },
    {
        q: 'Valley AI est-il inclus ?',
        a: "Oui, sur toutes les offres, avec un quota mensuel de requêtes : 20 (Explorer), 50 (Essentiel), 300 (Impact), 1 000 (Organisation), illimité (Institution). Votre solde du mois est visible directement dans l'assistant.",
    },
    {
        q: "Puis-je changer d'offre en cours de route ?",
        a: "Oui, à tout moment depuis votre espace. Les fonctionnalités sont activées immédiatement après upgrade. Pour un downgrade ou un cas particulier, contactez-nous — nous répondons sous 24 h.",
    },
    {
        q: "Que devient l'offre Consortium ?",
        a: "L'offre Consortium (hébergement de plusieurs organisations bénéficiaires pour les bailleurs et programmes) fait désormais partie de l'offre Institution, sur devis. Elle inclut tableau de bord consolidé, certificats Campus 360° et accompagnement ADRNS. Remplissez le formulaire de qualification sur la page Consortium.",
    },
    {
        q: 'Les paiements sont-ils sécurisés ?',
        a: "Tous les paiements sont traités par Stripe (PCI DSS Level 1, certification SOC 2). Aucune donnée de carte n'est stockée sur nos serveurs.",
    },
    {
        q: 'Acceptez-vous le paiement en FCFA / XOF / XAF ?',
        a: "Les prix sont affichés en multi-devise. Le paiement Stripe en ligne est en EUR. Le paiement Mobile Money (Orange Money, MTN MoMo, Wave) est en cours de développement — en attendant, le règlement en FCFA par virement bancaire est possible : contactez-nous.",
    },
    {
        q: 'Quelles sont vos références bailleurs ?',
        a: 'La plateforme respecte les exigences AFD, BAD, Banque mondiale, UE — sauvegardes ESS, audit trail immuable, KPIs financiers normalisés, exports conformes IFRS.',
    },
    {
        q: 'Mes données sont-elles hébergées en Afrique ?',
        a: "Vos données sont hébergées en Europe (Supabase EU) avec souveraineté RGPD complète. Une option d'hébergement Afrique (Dakar, Abidjan ou Lagos) est disponible pour l'offre Institution sur demande.",
    },
    {
        q: 'Que se passe-t-il si je résilie ?',
        a: 'Vous pouvez exporter toutes vos données à tout moment (JSON / Excel / PDF) depuis votre espace. Vos données sont conservées 90 jours après résiliation puis supprimées définitivement.',
    },
];

const fmt = (eur, currency) => {
    if (eur === null || eur === undefined) return null;
    const c = CURRENCIES.find(x => x.key === currency) || CURRENCIES[0];
    const val = Math.round(eur * c.rate);
    if (c.key === 'EUR') return `${val.toLocaleString('fr-FR')} €`;
    if (c.key === 'USD') return `$${val.toLocaleString('fr-FR')}`;
    return `${val.toLocaleString('fr-FR')} ${c.key}`;
};

const CellValue = ({ v, highlight }) => (
    v === true ? <Check size={16} className="text-primary mx-auto" /> :
    (v === false || v === '—') ? <span className="text-foreground/30">—</span> :
    <span className={`${String(v).includes('✓') ? 'text-primary font-medium' : 'text-foreground/75'} ${highlight ? 'font-medium' : ''}`}>{v}</span>
);

export default function Tarifs() {
    const [currency, setCurrency] = useState(() => localStorage.getItem('av_currency') || 'EUR');
    const [openFaq, setOpenFaq] = useState(0);
    const [loadingPlan, setLoadingPlan] = useState(null);
    const [plans, setPlans] = useState(PLAN_FALLBACK);
    const [trialDays, setTrialDays] = useState(14);
    const [openMobilePlan, setOpenMobilePlan] = useState('pro');

    useEffect(() => { localStorage.setItem('av_currency', currency); }, [currency]);
    useEffect(() => {
        import('@/lib/analytics').then((m) => m.trackEvent('tarifs_view'));
        fetchPlanCatalog().then(({ plans: p, trial_days }) => { setPlans(p); setTrialDays(trial_days); });
    }, []);

    const startStripeCheckout = async (planId) => {
        setLoadingPlan(planId);
        try {
            import('@/lib/analytics').then((m) => m.trackEvent('checkout_started', { plan: planId, currency }));
            const r = await axios.post(`${API}/billing/stripe/checkout`, {
                plan_id: planId, origin_url: window.location.origin,
            });
            if (r.data?.url) window.location.href = r.data.url;
        } catch (e) {
            // eslint-disable-next-line no-alert
            alert(`Impossible de démarrer le paiement : ${e?.response?.data?.detail || e?.message}`);
            setLoadingPlan(null);
        }
    };

    const planNames = plans.map(p => p.name);

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b-2 border-secondary bg-surface">
                <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="tarifs-home">
                        <img src="/brand/african-valley-logo-96.png" alt="African Valley 360°" className="h-11 w-11 rounded-lg object-cover" />
                        <span className="flex items-baseline gap-2">
                            <span className="font-heading text-2xl"><span className="text-foreground">African</span> <span className="text-primary">Valley</span></span>
                            <span className="font-heading text-xs uppercase tracking-[0.18em] text-foreground/50 hidden sm:inline">360°</span>
                        </span>
                    </Link>
                    <Link to="/auth/signin" className="text-sm text-foreground/70 hover:text-foreground" data-testid="tarifs-signin">Connexion →</Link>
                </div>
            </header>

            <main>
                {/* Hero */}
                <section className="container-page py-16 lg:py-20 text-center">
                    <div className="eyebrow mb-3">5 offres · une seule plateforme</div>
                    <h1 className="font-heading text-5xl lg:text-6xl mb-5" data-testid="tarifs-title">
                        Une offre pour chaque étape de <span className="text-primary">votre ambition</span>.
                    </h1>
                    <p className="text-lg text-foreground/70 max-w-2xl mx-auto leading-relaxed mb-8">
                        Du porteur de projet qui démarre gratuitement à l'institution qui pilote des centaines de projets —
                        African Valley 360° s'adapte à votre organisation. Sans engagement à long terme.
                    </p>

                    <div className="inline-flex items-center gap-2 bg-surface-muted/60 border border-border rounded-full p-1 shadow-soft" data-testid="tarifs-currency-switcher">
                        {CURRENCIES.map(c => (
                            <button key={c.key} onClick={() => setCurrency(c.key)}
                                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${currency === c.key ? 'bg-foreground text-background shadow-sm' : 'text-foreground/65 hover:text-foreground'}`}
                                data-testid={`tarifs-currency-${c.key}`}>
                                {c.label}
                            </button>
                        ))}
                    </div>
                    <p className="mt-4 text-xs text-foreground/60 max-w-xl mx-auto" data-testid="tarifs-mobile-money-note">
                        💳 <strong>Zone CFA</strong> : paiement Mobile Money (Orange Money, MTN MoMo, Wave) <strong>en cours de développement</strong> —
                        en attendant, règlement en FCFA par virement possible : <Link to="/p/contact" className="text-primary hover:underline">contactez-nous</Link>.
                    </p>
                </section>

                {/* 5 offres */}
                <section className="container-page pb-16">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5" data-testid="tarifs-plans-grid">
                        {plans.map((p) => (
                            <div key={p.tier}
                                data-testid={`tarifs-plan-${p.tier}`}
                                className={`card-soft p-6 flex flex-col relative ${p.recommended ? 'border-primary border-2 shadow-soft' : ''}`}>
                                {p.recommended && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.18em] font-bold whitespace-nowrap" data-testid="tarifs-badge-recommended">
                                        Recommandé
                                    </div>
                                )}
                                <div className="flex items-baseline gap-2 mb-2">
                                    <span className="text-xl">{p.emoji}</span>
                                    <span className="font-heading text-xl">{p.name}</span>
                                </div>
                                <div className="flex items-baseline gap-1.5 mb-1">
                                    <div className="font-heading text-3xl">
                                        {p.contact_only ? 'Sur devis' : p.monthly_eur ? fmt(p.monthly_eur, currency) : 'Gratuit'}
                                    </div>
                                    {p.monthly_eur ? <div className="text-sm text-muted-foreground">/mois</div> : null}
                                </div>
                                {p.yearly_eur ? (
                                    <div className="text-xs text-secondary font-medium mb-2">
                                        ou {fmt(p.yearly_eur, currency)} / an · 2 mois offerts
                                    </div>
                                ) : p.tier === 'free' ? (
                                    <div className="text-xs text-secondary font-medium mb-2">Permanent · sans carte bancaire</div>
                                ) : <div className="mb-2" />}
                                <div className="text-xs text-foreground/60 mb-4 leading-relaxed">{p.tagline}</div>
                                <ul className="space-y-1.5 text-[13px] mb-5 flex-1">
                                    {(p.features || []).map((f, i) => (
                                        <li key={i} className="flex items-start gap-2 text-foreground/75">
                                            <Check size={13} className="text-secondary mt-0.5 flex-shrink-0" /> <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                {p.plan_ids && (
                                    <>
                                        <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-secondary bg-secondary/10 border border-secondary/30 rounded-full px-2.5 py-1 self-start"
                                            data-testid={`tarifs-trial-badge-${p.tier}`}>
                                            ✨ {trialDays} jours d'essai gratuit
                                        </div>
                                        <Button
                                            onClick={() => startStripeCheckout(p.plan_ids.monthly)}
                                            disabled={!!loadingPlan}
                                            data-testid={`tarifs-stripe-monthly-${p.tier}`}
                                            className={`w-full mb-2 ${p.recommended ? 'bg-primary hover:bg-primary-hover text-primary-foreground' : 'bg-secondary hover:bg-secondary/90 text-secondary-foreground'}`}>
                                            {loadingPlan === p.plan_ids.monthly ? 'Redirection…' : `S'abonner · ${fmt(p.monthly_eur, currency)} /mois`}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={() => startStripeCheckout(p.plan_ids.yearly)}
                                            disabled={!!loadingPlan}
                                            data-testid={`tarifs-stripe-yearly-${p.tier}`}
                                            className="w-full mb-2">
                                            {loadingPlan === p.plan_ids.yearly ? 'Redirection…' : `Annuel · ${fmt(p.yearly_eur, currency)} /an`}
                                        </Button>
                                    </>
                                )}
                                <Link to={p.cta_to || '/p/contact'} className="block mt-auto">
                                    <Button variant={p.plan_ids ? 'outline' : 'default'}
                                        data-testid={`tarifs-cta-${p.tier}`}
                                        className={`w-full ${p.plan_ids ? 'border-foreground/20 hover:bg-surface-muted' : p.recommended ? 'bg-primary hover:bg-primary-hover' : 'bg-foreground hover:bg-foreground/90 text-background'}`}>
                                        {p.plan_ids ? 'Être accompagné' : p.cta} <ArrowRight size={14} className="ml-1.5" />
                                    </Button>
                                </Link>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Institution / Consortium */}
                <section className="container-page pb-16" data-testid="tarifs-institution-consortium">
                    <div className="card-soft p-7 lg:p-9 border-2 border-secondary/40 bg-surface flex flex-col lg:flex-row gap-6 items-start">
                        <div className="flex-1">
                            <div className="eyebrow mb-2">Bailleurs · Programmes · Consortiums</div>
                            <h2 className="font-heading text-2xl mb-2">🏛️ L'offre Consortium fait partie d'Institution</h2>
                            <p className="text-foreground/70 leading-relaxed text-sm">
                                Vous financez ou coordonnez plusieurs organisations ? L'offre <strong>Institution</strong> (sur devis) inclut
                                l'hébergement de vos organisations bénéficiaires, un tableau de bord consolidé, les certificats
                                Campus 360° et l'accompagnement ADRNS. Décrivez votre programme — nous construisons le devis avec vous.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                            <Link to="/p/consortium">
                                <Button variant="outline" data-testid="tarifs-consortium-cta">Qualifier mon programme →</Button>
                            </Link>
                            <Link to="/p/contact">
                                <Button className="bg-secondary hover:bg-secondary/90 text-secondary-foreground" data-testid="tarifs-institution-cta">
                                    Demander un devis
                                </Button>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Trust band */}
                <section className="bg-surface border-y border-border py-10">
                    <div className="container-page flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-sm text-foreground/65">
                        <span className="inline-flex items-center gap-2"><ShieldCheck size={16} className="text-primary" /> Paiement Stripe sécurisé (PCI DSS L1)</span>
                        <span className="inline-flex items-center gap-2"><Check size={16} className="text-primary" /> Sans engagement</span>
                        <span className="inline-flex items-center gap-2"><Check size={16} className="text-primary" /> Données RGPD · hébergement EU</span>
                        <span className="inline-flex items-center gap-2"><Check size={16} className="text-primary" /> Export complet à tout moment</span>
                    </div>
                </section>

                {/* Comparateur */}
                <section className="container-page py-16 lg:py-20">
                    <div className="text-center mb-12">
                        <div className="eyebrow mb-2">Comparateur détaillé</div>
                        <h2 className="font-heading text-3xl lg:text-4xl">Toutes les capacités, offre par offre.</h2>
                    </div>

                    {/* Desktop : tableau */}
                    <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full text-sm border-collapse" data-testid="tarifs-matrix">
                            <thead>
                                <tr className="border-b-2 border-border">
                                    <th className="text-left py-4 pr-4 font-medium text-foreground/65">Fonctionnalité</th>
                                    {plans.map((p, i) => (
                                        <th key={p.tier} className={`py-4 px-3 font-heading text-base ${p.recommended ? 'text-primary' : ''}`}>
                                            {p.name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {COMPARATOR.map((group) => (
                                    <React.Fragment key={group.group}>
                                        <tr className="bg-surface-muted/50">
                                            <td colSpan={6} className="py-2 px-2 font-semibold text-xs uppercase tracking-wider text-foreground/55">
                                                {group.group}
                                            </td>
                                        </tr>
                                        {group.rows.map((row, idx) => (
                                            <tr key={idx} className="border-b border-border/40">
                                                <td className="py-3 pr-4 text-foreground/85">{row.label}</td>
                                                {row.plans.map((v, ci) => (
                                                    <td key={ci} className={`py-3 px-3 text-center text-sm ${plans[ci]?.recommended ? 'bg-primary-light/30' : ''}`}>
                                                        <CellValue v={v} highlight={plans[ci]?.recommended} />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile / tablette : accordéons par offre */}
                    <div className="lg:hidden space-y-3" data-testid="tarifs-matrix-mobile">
                        {plans.map((p, colIdx) => (
                            <div key={p.tier} className={`rounded-xl border overflow-hidden ${p.recommended ? 'border-primary/50' : 'border-border'} bg-white`}>
                                <button
                                    onClick={() => setOpenMobilePlan(openMobilePlan === p.tier ? null : p.tier)}
                                    className="w-full px-5 py-4 flex items-center justify-between gap-3 text-left"
                                    data-testid={`tarifs-matrix-mobile-${p.tier}`}>
                                    <span className="flex items-center gap-2">
                                        <span>{p.emoji}</span>
                                        <span className="font-heading text-lg">{p.name}</span>
                                        {p.recommended && <span className="text-[9px] uppercase tracking-wider bg-primary text-primary-foreground rounded-full px-2 py-0.5 font-bold">Recommandé</span>}
                                    </span>
                                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                                        {p.contact_only ? 'Sur devis' : p.monthly_eur ? `${fmt(p.monthly_eur, currency)}/mois` : 'Gratuit'}
                                        <ChevronDown size={16} className={`transition-transform ${openMobilePlan === p.tier ? 'rotate-180' : ''}`} />
                                    </span>
                                </button>
                                {openMobilePlan === p.tier && (
                                    <div className="px-5 pb-5 border-t border-border/60">
                                        {COMPARATOR.map((group) => (
                                            <div key={group.group} className="mt-4">
                                                <div className="text-[11px] uppercase tracking-wider font-semibold text-foreground/50 mb-2">{group.group}</div>
                                                <div className="space-y-1.5">
                                                    {group.rows.map((row, idx) => (
                                                        <div key={idx} className="flex items-center justify-between gap-3 text-sm py-1 border-b border-border/30">
                                                            <span className="text-foreground/80">{row.label}</span>
                                                            <span className="shrink-0"><CellValue v={row.plans[colIdx]} /></span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                {/* FAQ */}
                <section className="bg-surface-muted/30 py-16 lg:py-20">
                    <div className="container-page max-w-3xl">
                        <div className="text-center mb-12">
                            <div className="eyebrow mb-2 inline-flex items-center gap-1.5"><HelpCircle size={12} /> Questions fréquentes</div>
                            <h2 className="font-heading text-3xl lg:text-4xl">Tout ce que vous devez savoir avant de choisir.</h2>
                        </div>
                        <div className="space-y-3" data-testid="tarifs-faq">
                            {FAQ.map((item, i) => (
                                <div key={i} className={`rounded-xl border ${openFaq === i ? 'bg-white border-primary/30 shadow-soft' : 'bg-white/60 border-border'} overflow-hidden`}>
                                    <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                                        className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 hover:bg-surface-muted/40"
                                        data-testid={`tarifs-faq-${i}`}>
                                        <span className="font-medium text-foreground">{item.q}</span>
                                        <span className={`text-primary text-xl transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                                    </button>
                                    {openFaq === i && (
                                        <div className="px-5 pb-5 text-sm text-foreground/70 leading-relaxed">{item.a}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Final CTA */}
                <section className="container-page py-16 lg:py-20 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-light text-primary text-xs uppercase tracking-[0.18em] font-medium mb-6">
                        <Sparkles size={12} /> Encore une question ?
                    </div>
                    <h2 className="font-heading text-3xl lg:text-4xl mb-5">
                        Parlons de votre projet en <span className="text-primary">15 minutes</span>.
                    </h2>
                    <p className="text-foreground/70 max-w-xl mx-auto mb-8">
                        Nos architectes solutions vous aident à choisir la bonne offre, sans engagement ni pression commerciale.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <Link to="/p/contact">
                            <Button size="lg" className="bg-primary hover:bg-primary-hover h-12 px-7" data-testid="tarifs-contact-rdv">
                                📅 Réserver un rendez-vous
                            </Button>
                        </Link>
                        <Link to="/p/partenaires">
                            <Button size="lg" variant="outline" className="h-12 px-7" data-testid="tarifs-partenaires">
                                🏦 Banques & institutions — Espace Partenaires
                            </Button>
                        </Link>
                        <Link to="/signup-free">
                            <Button size="lg" variant="outline" className="h-12 px-7" data-testid="tarifs-signup-free">
                                Commencer gratuitement <ArrowRight size={14} className="ml-1.5" />
                            </Button>
                        </Link>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
